// Builds the ReportItContext for the Report It dialog (MV-1077).
//
// The dialog is opened from the profile dropdown, which appears BOTH inside an
// experience (under MytaverseEventProvider) and on the event-selection screen
// (where that provider is unmounted). `useMytaverseEvent()` returns undefined
// outside the experience, so this hook picks:
//   - LIVE context in-experience: the GameCast video element + getVideoRTCStats
//     + live stream ids, for a rich black-screen snapshot.
//   - CACHED context on the event screen: the last experience attempt's ids +
//     failure reason from experienceDiagnostics, so a "couldn't enter" report
//     still carries everything the backend needs to correlate.
//
// Provider-coupled, so it lives with the dialog rather than in services/reportIt
// (which stays decoupled + unit-testable on a plain context object).

import { useMemo } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { env } from 'env';

import { ReportItContext } from '@services/reportIt/useReportIt';
import { readExperienceDiagnostics } from '@services/reportIt/experienceDiagnostics';

const GAMECAST_VIDEO_ELEMENT_ID = 'gamecastVideoTag';

const getVideoElementFromDom = (): HTMLVideoElement | null =>
  document.getElementById(GAMECAST_VIDEO_ELEMENT_ID) as HTMLVideoElement | null;

export interface ReportItContextResult {
  context: ReportItContext;
  /** True when built from a live in-experience session (rich snapshot available). */
  hasLiveExperience: boolean;
  /** The cached reason a prior experience attempt ended (event-screen reports). */
  failureReason?: string;
}

export const useReportItContext = (): ReportItContextResult => {
  const { websocketSessionId } = useMytaverse();
  // Falsy outside the experience (event-selection screen) — see MainScreen's
  // `isOnDashboard` usage. The same dialog therefore works in both places.
  const event = useMytaverseEvent();

  const eventId = event?.currentEvent?.eventId || event?.currentEventId;
  const gameCastStreamId = event?.gameCastStreamId;
  const gameCastStreamRegion = event?.gameCastStreamRegion;
  const gamecastClientRef = event?.gamecastClientRef;
  const videoRef = event?.videoRef;

  return useMemo<ReportItContextResult>(() => {
    const environment =
      env.REACT_APP_MYTAVERSE_ENV || env.REACT_APP_SENTRY_ENV || 'unknown';
    const browserSessionId = websocketSessionId || 'unknown-session';

    if (event) {
      const context: ReportItContext = {
        environment,
        eventId,
        browserSessionId,
        entities: {
          // Merge the cached attempt entities so the live report also carries
          // the stream-session ARN (held only in the GameCast ref, not context).
          ...(readExperienceDiagnostics()?.entities ?? {}),
          websocketSessionId,
          streamGroupId: gameCastStreamId || undefined,
          region: gameCastStreamRegion || undefined,
        },
        // Merge video + audio inbound-rtp into ONE report so the snapshot
        // captures BOTH series. Audio still flowing while the video track
        // stalls is the canonical black-screen / "laggy video" signal — the
        // analysis needs the audio series to confirm it (MV-1077). Previously
        // only getVideoRTCStats was wired, so the audio series was always
        // empty and the analyser reported "audio.activity missing".
        getStats: gamecastClientRef?.current?.getVideoRTCStats
          ? async (): Promise<RTCStatsReport> => {
              const client = gamecastClientRef.current;
              const merged = new Map<string, unknown>();
              const [video, audio] = await Promise.all([
                client?.getVideoRTCStats?.(),
                client?.getAudioRTCStats?.(),
              ]);
              video?.forEach((value: unknown, key: string) =>
                merged.set(key, value),
              );
              audio?.forEach((value: unknown, key: string) =>
                merged.set(key, value),
              );
              return merged as unknown as RTCStatsReport;
            }
          : undefined,
        getVideoElement: () => videoRef?.current ?? getVideoElementFromDom(),
      };

      return { context, hasLiveExperience: true };
    }

    // Outside the experience: rebuild from the cached last attempt so a report
    // about "couldn't enter the experience" still carries the streaming
    // correlation ids + the failure reason. No live video/getStats here — the
    // snapshot degrades to "video not present", which is correct (no stream).
    const diagnostics = readExperienceDiagnostics();

    const context: ReportItContext = {
      environment,
      eventId: diagnostics?.eventId,
      browserSessionId,
      entities: {
        ...(diagnostics?.entities ?? {}),
        websocketSessionId:
          websocketSessionId || diagnostics?.entities.websocketSessionId,
      },
      getVideoElement: () => getVideoElementFromDom(),
    };

    return {
      context,
      hasLiveExperience: false,
      failureReason: diagnostics?.failureReason,
    };
  }, [
    event,
    eventId,
    gameCastStreamId,
    gameCastStreamRegion,
    gamecastClientRef,
    videoRef,
    websocketSessionId,
  ]);
};
